import {deferToIdle} from '../utils/deferToIdle.js';
import {ensureTurnstileLoaded, renderTurnstile} from '../modules/turnstile.js';
import {Notification} from '../modules/notification.js';
import {t} from '../localization/translate.js';

/**
 * Lazy-loads Turnstile for the upcoming newsletter card form.
 * Loads the captcha only when the user focuses the email input.
 */
class NewsletterCardForm {
    static selector = {
        form: '.js-newsletterCardForm',
        emailInput: 'input[type="email"]',
    };

    /** @param {HTMLFormElement} formElement */
    constructor(formElement) {
        this.form = formElement;

        /** @type {HTMLInputElement|null} */
        this.emailInput = this.form.querySelector(NewsletterCardForm.selector.emailInput);

        if (this.emailInput) {
            this.emailInput.addEventListener('focus', this.#loadTurnstile.bind(this), {once: true});
        }
    }

    /** Lazy-loads Turnstile when the user focuses the email input. */
    #loadTurnstile() {
        const turnstileElement = this.form.querySelector('.cf-turnstile');
        if (turnstileElement instanceof HTMLElement) {
            ensureTurnstileLoaded()
                .then(() => renderTurnstile(turnstileElement))
                .catch((error) => {
                    Notification.error(t('Captcha failed to load: :reason', {reason: error.message}));
                });
        }
    }

    /**
     * @param {Document | Element} [root]
     */
    static init(root = document) {
        const forms = root.querySelectorAll(NewsletterCardForm.selector.form);
        forms.forEach((form) => {
            if (!(form instanceof HTMLFormElement) || form.dataset.newsletterCardFormInitialized === 'true') {
                return;
            }

            form.dataset.newsletterCardFormInitialized = 'true';
            new NewsletterCardForm(form);
        });
    }
}

/** Initializes newsletter card forms, deferred until the browser is idle. */
function initializeNewsletterCardForms() {
    deferToIdle(() => NewsletterCardForm.init());
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initializeNewsletterCardForms)
    : initializeNewsletterCardForms();

document.addEventListener('htmx:afterSwap', (event) => {
    const target = event.target;

    if (target instanceof Element) {
        NewsletterCardForm.init(target);
        return;
    }

    NewsletterCardForm.init();
});
